import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import type { AuditEvent } from "../../shared/types";
import { ErrorBanner, Field } from "../components/ui";
import type { ApiError } from "../lib/api";
import { get } from "../lib/api";
import { AuditTable, PageHeader, toApiError } from "./common";

const PAGE = 100;

const ENTITIES: [string, string][] = [
  ["", "Everything"],
  ["visit", "Visits"],
  ["site", "Sites"],
  ["person", "People"],
  ["settings", "Settings"],
  ["schedule", "Schedules"],
  ["export_batch", "Exports"],
  ["qb_connection", "QuickBooks"],
  ["retention", "Retention clean-up"],
];

export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const entity = params.get("entity") ?? "";
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [more, setMore] = useState(false);

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams({ limit: String(PAGE) });
        if (entity) q.set("entity", entity);
        if (before) q.set("before", String(before));
        const page = await get<AuditEvent[]>(`/admin/audit?${q}`);
        setEvents((prev) => (before ? [...prev, ...page] : page));
        setMore(page.length === PAGE);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [entity],
  );

  useEffect(() => {
    setEvents([]);
    void load();
  }, [load]);

  const last = events[events.length - 1];

  return (
    <div className="page">
      <PageHeader
        title="Audit log"
        sub="Every change to visits, sites, people and settings. Nothing here can be edited."
      />
      <div className="card flat" style={{ marginBottom: 12, maxWidth: 360 }}>
        <Field label="Show">
          <select
            value={entity}
            onChange={(e) => setParams(e.target.value ? { entity: e.target.value } : {}, { replace: true })}
          >
            {ENTITIES.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {error && <ErrorBanner error={error} onRetry={() => void load(last?.id)} />}
      {events.length > 0 || !loading ? <AuditTable events={events} /> : null}
      <div className="row" style={{ marginTop: 12 }}>
        {loading && <span className="muted">Loading…</span>}
        {!loading && more && last && (
          <button className="btn" onClick={() => void load(last.id)}>
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
